import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { registerSW } from "virtual:pwa-register";
import type { Update } from "@tauri-apps/plugin-updater";
import "./aviso-de-versao.css";

/**
 * Aviso discreto de versão nova, no site e no aplicativo do computador.
 *
 * **No site** o service worker é registrado em modo "prompt": a versão nova
 * baixa sozinha e fica esperando em segundo plano, sem trocar nada embaixo de
 * quem está no meio de uma rodada. Este cartão avisa e a troca só acontece
 * quando o jogador manda. Recusar apenas esconde o aviso: a versão nova assume
 * na próxima vez que o jogo abrir.
 *
 * **No computador** quem atualiza é o atualizador do Tauri. Ele pergunta ao
 * servidor de releases ao abrir e a cada seis horas; havendo versão maior, o
 * mesmo cartão aparece. Aceitar baixa o instalador assinado, confere a
 * assinatura com a chave pública do `tauri.conf.json` e roda o instalador em
 * modo passivo, que fecha o jogo e o abre de novo já atualizado. Antes disto o
 * plugin estava registrado mas ninguém perguntava nada, e o jogo instalado
 * nunca se atualizava.
 *
 * Em desenvolvimento o plugin do PWA devolve um `registerSW` que não faz nada,
 * porque cache durante o `pnpm dev` só serviria para esconder as mudanças.
 */

const SEIS_HORAS = 6 * 60 * 60 * 1000;

const noComputador = (): boolean => typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

export function AvisoDeVersao(): ReactNode {
  const [visivel, setVisivel] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [versao, setVersao] = useState<string | null>(null);
  const aplicar = useRef<((recarregar?: boolean) => Promise<void>) | null>(null);
  const atualizacao = useRef<Update | null>(null);

  useEffect(() => {
    if (noComputador()) {
      let vivo = true;
      const perguntar = async (): Promise<void> => {
        try {
          const { check } = await import("@tauri-apps/plugin-updater");
          const nova = await check();
          if (!vivo || !nova) return;
          atualizacao.current = nova;
          setVersao(nova.version);
          setVisivel(true);
        } catch {
          /* sem rede ou servidor fora do ar: pergunta de novo mais tarde */
        }
      };
      void perguntar();
      const relogio = window.setInterval(() => void perguntar(), SEIS_HORAS);
      return () => {
        vivo = false;
        window.clearInterval(relogio);
      };
    }

    // O StrictMode roda o efeito duas vezes em desenvolvimento, e registrar o
    // mesmo service worker duas vezes na mesma página não traria nada de bom.
    if (aplicar.current) return;
    aplicar.current = registerSW({
      immediate: true,
      onNeedRefresh: () => setVisivel(true),
    });
    return undefined;
  }, []);

  const atualizar = useCallback(() => {
    setAplicando(true);
    if (atualizacao.current) {
      void atualizacao.current.downloadAndInstall().catch(() => setAplicando(false));
      return;
    }
    void aplicar.current?.(true);
  }, []);

  if (!visivel) return null;

  return (
    <aside className="aviso-versao card" role="status" aria-live="polite">
      <div>
        <span className="label">Atualização</span>
        <p className="aviso-versao-texto">
          {versao
            ? `A versão ${versao} do Legacy Manager está pronta para instalar. Suas carreiras salvas continuam onde estão.`
            : "Uma versão nova do Legacy Manager já está baixada. Suas carreiras salvas continuam onde estão."}
        </p>
      </div>
      <div className="aviso-versao-acoes">
        <button type="button" className="ghost" onClick={() => setVisivel(false)}>
          Depois
        </button>
        <button type="button" className="primary" onClick={atualizar} disabled={aplicando}>
          {aplicando ? "Atualizando" : "Atualizar agora"}
        </button>
      </div>
    </aside>
  );
}
